import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Navigation, Truck } from "lucide-react";
import { waLink, DEFAULT_QUOTE_MESSAGE, IMAGES } from "../../lib/site";
import { RevealLine, WhatsAppIcon } from "./bits";

const chips = [
    { icon: Truck, label: "Frota própria" },
    { icon: MapPin, label: "Rastreio em tempo real" },
    { icon: Navigation, label: "Cobertura nacional" },
];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const cardY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

    return (
        <section
            id="topo"
            data-testid="hero-section"
            ref={ref}
            className="relative flex min-h-svh items-center overflow-hidden pt-20"
        >
            <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
                <img
                    src={IMAGES.heroBg}
                    alt=""
                    className="h-[115%] w-full object-cover opacity-35 grayscale contrast-125"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/80 to-ink/30" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/70" />
                <div className="absolute inset-0 bg-magenta-glow mix-blend-screen" />
            </motion.div>

            <div className="mx-auto w-full max-w-7xl px-5 pb-24 lg:px-8">
                <RevealLine delay={0.15}>
                    <span className="mb-6 inline-flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-tangerine md:text-xs">
                        <span className="checkers inline-block h-3 w-3" />
                        Transporte rodoviário de cargas
                    </span>
                </RevealLine>

                <h1
                    data-testid="hero-main-title"
                    className="font-display text-[clamp(3.4rem,11vw,9rem)] font-extrabold uppercase italic leading-[0.9] tracking-tight"
                >
                    <RevealLine delay={0.28}>
                        <span className="text-mist">Transportando</span>
                    </RevealLine>
                    <RevealLine delay={0.42}>
                        <span className="text-outline">com</span>{" "}
                        <span className="bg-gradient-to-r from-magenta via-magenta to-tangerine bg-clip-text text-transparent">
                            Excelência
                        </span>
                    </RevealLine>
                </h1>

                <RevealLine delay={0.6}>
                    <p className="mt-8 max-w-xl text-base leading-relaxed text-mute md:text-lg">
                        Cargas fracionadas, lotação dedicada, armazenagem e
                        distribuição última milha. Com sede em Brasília (DF) e
                        atendimento em todo o Brasil, tratamos cada entrega como
                        compromisso.
                    </p>
                </RevealLine>

                <RevealLine delay={0.72}>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a
                            href={waLink(DEFAULT_QUOTE_MESSAGE)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="hero-whatsapp-quote-button"
                            className="skew-cta inline-flex h-14 items-center gap-3 bg-magenta px-8 text-base font-semibold text-white shadow-[0_0_40px_rgba(176,38,214,0.45)] transition-all duration-300 hover:bg-magenta-deep hover:shadow-[0_0_60px_rgba(176,38,214,0.6)]"
                        >
                            <span className="unskew flex items-center gap-3">
                                <WhatsAppIcon className="h-5 w-5" />
                                Pedir cotação no WhatsApp
                            </span>
                        </a>
                        <a
                            href="#servicos"
                            data-testid="hero-services-link"
                            className="inline-flex h-14 items-center gap-2 border border-white/15 px-8 text-sm font-medium text-mist/80 transition-colors duration-300 hover:border-tangerine hover:text-tangerine"
                        >
                            Ver serviços
                            <ArrowDown className="h-4 w-4" />
                        </a>
                    </div>
                </RevealLine>

                <RevealLine delay={0.85}>
                    <div className="mt-14 flex flex-wrap gap-3">
                        {chips.map(({ icon: Icon, label }) => (
                            <span
                                key={label}
                                className="inline-flex items-center gap-2 border border-white/10 bg-white/[0.03] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-mist/70"
                            >
                                <Icon className="h-3.5 w-3.5 text-magenta" />
                                {label}
                            </span>
                        ))}
                    </div>
                </RevealLine>
            </div>

            <motion.div
                style={{ y: cardY }}
                className="absolute bottom-24 right-10 hidden w-[330px] rotate-2 lg:block"
            >
                <div className="overflow-hidden border border-magenta/40 shadow-[0_20px_80px_rgba(176,38,214,0.25)]">
                    <img
                        src={IMAGES.heroCard}
                        alt="Caminhão da frota Borges Cargas em viagem ao entardecer"
                        className="h-56 w-full object-cover grayscale transition-all duration-700 hover:grayscale-0"
                    />
                    <div className="flex items-center justify-between bg-ink/90 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-mist/70">
                        <span>Frota Borges</span>
                        <span className="text-tangerine">Em rota</span>
                    </div>
                </div>
            </motion.div>

            <div className="absolute bottom-0 left-0 right-0 h-px bg-white/5" />
        </section>
    );
};
